<template>
  <div class="form-group">
    <label v-bind:for="id" v-if="label" class="form-label">{{ label }}
      <i v-if="required" class="far fa-asterisk"></i>
    </label>
    <div v-click-outside="close" v-bind:class="{'is-invalid': invalid}">
      <div v-on:click="$refs.input.focus()" v-bind:class="{'focus': focus}" style="cursor: text;" class="form-control">
        <div class="row g-0">
          <div v-for="(v, index) in (computedInputIndex >= 0 ? internalValue.slice(0, computedInputIndex) : internalValue)"
               v-bind:key="'pre' + index"
               class="col-auto align-self-center">
            <button v-on:click.prevent="remove(v)"
                    class="btn btn-sm btn-primary py-0 me-2 my-1">
              {{ objects ? v[objectName] : v }}<i class="ms-2 fal fa-times"/>
            </button>
          </div>
          <div class="col-auto align-self-center">
            <input ref="input"
                   type="text"
                   v-model="term"
                   v-on:focus="focus = true"
                   v-on:blur="focus = false"
                   v-on:keydown="inputKeydownListener"
                   class="form-control border-0 p-0 me-2"
                   v-bind:class="{'is-invalid': invalid}"
                   style="background-color: transparent; outline: none; box-shadow: none; display: inline-block; width: auto;"/>
          </div>
          <div v-for="(v, index) in (computedInputIndex >= 0 ? internalValue.slice(computedInputIndex) : [])"
               v-bind:key="'post' + index"
               class="col-auto align-self-center">
            <button v-on:click.prevent="remove(v)"
                    class="btn btn-sm btn-primary py-0 me-2 my-1">
              {{ objects ? v[objectName] : v }}<i class="ms-2 fal fa-times"/>
            </button>
          </div>
        </div>
      </div>
      <div class="dropdown">
        <ul class="dropdown-menu shadow"
            v-bind:class="{'show': loading || items.length}"
            style="position: absolute; width: 100%;">
          <li v-if="loading">
            <span class="dropdown-item"><i class="fa-loading-black-16"></i></span>
          </li>
          <li v-else="" v-for="(item, index) in items" v-bind:key="'item' + index">
            <a href="#"
               v-on:click.prevent="add(item)"
               class="dropdown-item"
               v-bind:class="{'active': itemIndex == index}">
              {{ objects ? item[objectName] : item }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <local-errors v-bind:value="errors"/>
    <div v-if="help" class="form-text">
      <slot/>
    </div>
  </div>
</template>

<script>
import Axios from "axios"
export default {
  name: "input-tags",
  props: {
    id: {
      type: String,
      required: true
    },
    value: {
      type: Array,
      required: true
    },
    /**
     * If true, you will be allowed to input any string you like. It does not have to be found among any of the
     * autocompleted items. Freeform does not work with objects enabled.
     */
    freeform: {
      type: Boolean,
      default: false
    },
    /**
     * If true that means the endpoint returns objects that have at least an id and name and that the value of
     * this component is an array of those ids and the visible tags are the names.
     */
    objects: {
      type: Boolean,
      default: false
    },
    /**
     * Name of the id in the objects we work with. Default is "id".
     */
    objectId: {
      type: String,
      default: "id"
    },
    /**
     * Name of the name in the objects we work with. Default is "name".
     */
    objectName: {
      type: String,
      default: "name"
    },
    /**
     * A function called whenever the search term changes. The function takes the term as a parameter.
     */
    termChanged: {
      type: Function
    },
    /**
     * The URL for the component's endpoint. The endpoint is called with a GET request and the term parameter,
     * when doing autocompletes. If working with objects the endpoint is also called with one or more id parameters,
     * to look up objects.
     * An endpoint is required if you work with objects as there otherwise would not be any way of looking up objects.
     */
    endpoint: {
      type: String
    },
    termParameter: {
      type: String,
      default: "term"
    },
    idParameter: {
      type: String,
      default: "id"
    },
    /**
     * Delay in seconds between typing something and autocompleting it. Setting this too low will send too many
     * requests to the endpoint.
     */
    timeout: {
      type: Number,
      default: 100
    },
    errors: {
      type: Array
    },
    label: {
      type: String
    },
    required: {
      type: Boolean
    },
  },
  data: function () {
    return {
      internalValue: [],
      focus: false,
      term: "",
      inputIndex: null,
      loading: 0,
      items: [],
      itemIndex: null,
      abortControllers: []
    }
  },
  created: function () {
    document.addEventListener("keydown", this.globalKeydownListener);
  },
  destroyed: function () {
    document.removeEventListener("keydown", this.globalKeydownListener);
  },
  computed: {
    help: function () {
      return !!this.$slots.default;
    },
    invalid: function () {
      return this.errors && this.errors.length > 0;
    },
    computedInputIndex: {
      get: function () {
        if (this.inputIndex == null) {
          return this.internalValue.length;
        }
        return this.inputIndex;
      },
      set: function (newValue) {
        if (newValue < 0) {
          newValue = 0;
        } else if (newValue >= this.internalValue.length) {
          newValue = null;
        }
        this.inputIndex = newValue;
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler:function (newValue) {
        var instance = this;
        if (instance.objects) {
          // Remove from the internal value any objects not found in the new value.
          internalValue: for (var k = 0; k < instance.internalValue.length; k++) {
            for (var l = 0; l < newValue.length; l++) {
              if (instance.internalValue[k][instance.objectId] == newValue[l]) {
                continue internalValue;
              }
            }
            instance.internalValue.splice(k, 1);
            k--;
          }
          var url = instance.endpoint;
          var params = new URLSearchParams();
          newValue: for (var i = 0; i < newValue.length; i++) {
            // Check if we already have the value in our internal value. If we do, there is no need to load it again.
            for (var j = 0; j < instance.internalValue.length; j++) {
              if (newValue[i] == instance.internalValue[j][instance.objectId]) {
                continue newValue;
              }
            }
            params.append(instance.objectId + "[]", newValue[i]);
          }
          Axios(
              {
                url: url,
                method: "get",
                params: params
              }
          ).then(function (response) {
            // Cache everything we just loaded and add the objects to the internal value.
            for (var i = 0; i < response.data.length; i++) {
              var obj = response.data[i];
              instance.internalValue.push(obj);
            }
            // Sort the internal value so it matches the order of items in value.
            instance.internalValue.sort(function (a, b) {
              var aIndex = instance.value.indexOf(instance.objects ? a[instance.objectId] : a);
              var bIndex = instance.value.indexOf(instance.objects ? b[instance.objectId] : b);
              if (aIndex < bIndex) {
                return -1;
              }
              if (aIndex > bIndex) {
                return 1;
              }
              return 0;
            });
          }).catch(function (err) {
            console.log(err);
          });
        } else {
          instance.internalValue = newValue;
        }
      }
    },
    term: function (newValue) {
      if (typeof this.termChanged === "function") {
        this.termChanged(newValue);
      }
      // Abort previous load of items before we load items for the new term.
      var controllers = this.abortControllers.splice(0, this.abortControllers.length);
      for (var i = 0; i < controllers.length; i++) {
        controllers[i].abort();
      }
      this.loadItems();
    }
  },
  methods: {
    loadItems: function () {
      var instance = this;
      instance.loading++;
      // This splice is so we do not see any of previous results once we do a new search.
      instance.items.splice(0, instance.items.length);
      var controller = new AbortController();
      var encodedTerm = encodeURIComponent(instance.term);
      var url = instance.endpoint;
      if (!url.endsWith("?" + instance.termParameter + "=") && !url.endsWith("&" + instance.termParameter + "=")) {
        url += url.indexOf("?") == -1 ? "?" : "&";
        url += instance.termParameter + "=" + encodedTerm;
      }
      setTimeout(function () {
        Axios(
            {
              signal: controller.signal,
              url: url,
              method: "get"
            }
        ).then(function (response) {
          // If the term hasn't changed this is the response we are looking for.
          if (encodedTerm == encodeURIComponent(instance.term) && response.data && Array.isArray(response.data)) {
            instance.items = response.data;
          }
        }).catch(function (err) {
          console.log(err);
        }).then(function () {
          instance.loading--;
        });
      }, instance.timeout);
      this.abortControllers.push(controller);
    },
    close: function () {
      this.computedInputIndex = null;
      if (this.items.length > 0) {
        this.items.splice(0, this.items.length);
      }
      this.itemIndex = null;
    },
    reset: function () {
      this.term = "";
      this.close();
    },
    add: function (item) {
      if (item) {
        this.remove(item);
        var array = this.value;
        if (this.objects) {
          array.push(item[this.objectId]);
        } else {
          array.push(item);
        }
        this.updateValue(array);
        this.reset();
        this.inputFocus();
      }
    },
    remove: function (item) {
      if (item) {
        var array = this.value;
        for (var i = 0; i < array.length; i++) {
          var existingId = array[i];
          var itemId = this.objects ? item[this.objectId] : item;
          if (itemId == existingId) {
            array.splice(i, 1);
            i--;
          }
        }
        this.updateValue(array);
      }
    },
    isCursorAtBeginning: function () {
      return this.$refs.input.selectionStart == 0 && this.$refs.input.selectionEnd == 0;
    },
    isCursorAtEnd: function () {
      var lastIndex = this.term.length;
      return this.$refs.input.selectionStart == lastIndex && this.$refs.input.selectionEnd == lastIndex;
    },
    inputFocus: function () {
      var instance = this;
      instance.$nextTick(function () {
        instance.$refs.input.focus();
      });
    },
    inputKeydownListener: function (event) {
      // Arrow back - if the cursor is on the first position, move the text input to before the previous item
      if (event.keyCode == 37 && this.isCursorAtBeginning()) {
        this.computedInputIndex--;
        this.inputFocus();
      }
      // Arrow forward - if the cursor is on the last position, move the text input to after the next item
      else if (event.keyCode == 39 && this.isCursorAtEnd()) {
        this.computedInputIndex++;
        this.inputFocus();
      }
      // Arrow up - select previous item in the dropdown
      else if (event.keyCode == 38) {
        if (this.items.length > 0) {
          if (this.itemIndex > 0) {
            this.itemIndex--;
          } else {
            this.itemIndex = this.items.length - 1;
          }
        }
      }
      // Arrow down - select next item in the dropdown
      else if (event.keyCode == 40) {
        if (this.items.length > 0) {
          if (this.itemIndex != null && this.itemIndex < this.items.length - 1) {
            this.itemIndex++;
          } else {
            this.itemIndex = 0;
          }
        }
      }
      // Enter
      else if (event.keyCode == 13) {
        // If an item is selected in the dropdown, add it
        if (this.itemIndex != null && this.items.length > this.itemIndex) {
          this.add(this.items[this.itemIndex]);
        }
            // Otherwise check if an item matches the text in the input field and then add that item.
        // Or if we are not using objects and you are allowed to type anything, then just add whatever was typed.
        else {
          if (this.term.trim().length > 0) {
            if (!this.objects && this.freeform) {
              this.add(this.term);
            } else {
              for (var i = 0; i < this.items.length; i++) {
                var item = this.items[i];
                if (item
                    && (
                        (this.objects && this.term.toLowerCase() == item[this.objectName].toLowerCase())
                        || (!this.objects && this.term.toLowerCase() == item.toLowerCase())
                    )
                ) {
                  this.add(item);
                  break;
                }
              }
            }
          }
        }
      }
      // Backspace - if the cursor is on the first position delete the previous item
      else if (event.keyCode == 8 && this.computedInputIndex && this.isCursorAtBeginning()) {
        this.computedInputIndex--;
        this.remove(this.internalValue[this.computedInputIndex]);
        this.inputFocus();
      }
      // Delete - if cursor is on last position delete the next item
      else if (event.keyCode == 46 && this.isCursorAtEnd()) {
        this.remove(this.internalValue[this.computedInputIndex]);
        this.inputFocus();
      }
    },
    globalKeydownListener: function (event) {
      if (event.keyCode == 27) {
        this.close();
      }
    },
    updateValue: function (value) {
      this.$emit("input", value);
    }
  }
}
</script>
